/**
 * Instagram Browser Detection and Optimization
 * Ensures all templates work perfectly in Instagram in-app browser
 */

/**
 * Detect if user is using Instagram / Meta in-app browser (IAB).
 * Covers links opened from Instagram (bio, stories, DMs), Facebook in-app, Threads,
 * and Chromium UA-CH brand hints when available.
 */
export function isInstagramBrowser(): boolean {
  if (typeof window === "undefined") return false;

  const ua = navigator.userAgent.toLowerCase();
  const ref = (typeof document !== "undefined" && document.referrer)
    ? document.referrer.toLowerCase()
    : "";

  // Chromium Client Hints brands (Instagram / Facebook / Threads often appear here)
  try {
    const uad = (
      navigator as Navigator & {
        userAgentData?: { brands?: { brand: string }[] };
      }
    ).userAgentData;
    const brands = uad?.brands;
    if (
      brands?.some((b) =>
        /instagram|facebook|threads|messenger/i.test(b.brand),
      )
    ) {
      return true;
    }
  } catch {
    /* ignore */
  }

  // Instagram Explorer / in-app WebView – UA markers
  if (ua.includes("instagram")) return true;
  if (ua.includes("threads")) return true; // Meta Threads in-app
  if (ua.includes("iabmv")) return true; // Instagram in-app browser marker (e.g. IABMV/1)
  if (ua.includes("fban") || ua.includes("fbav")) return true; // Meta app (Instagram/Facebook)
  if (ua.includes("fb_iab")) return true; // Facebook IAB marker (some builds)

  // Referrer fallback: opened from Instagram (bio, story, link sticker, etc.)
  if (
    ua.includes("mobile") &&
    ua.includes("webkit") &&
    (ref.includes("instagram") ||
      ref.includes("facebook.com") ||
      ref.includes("threads.net"))
  ) {
    return true;
  }

  return false;
}

/** Alias for clarity when handling Instagram Explorer specifically. */
export const isInstagramExplorer = isInstagramBrowser;

/**
 * Detect mobile device
 */
export function isMobileDevice(): boolean {
  if (typeof window === "undefined") return false;

  const userAgent = navigator.userAgent.toLowerCase();
  return (
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent) ||
    window.innerWidth <= 768
  );
}

/**
 * Get Instagram browser optimizations
 */
export function getInstagramOptimizations() {
  const isInstagram = isInstagramBrowser();
  const isMobile = isMobileDevice();

  return {
    isInstagramBrowser: isInstagram,
    isMobile,
    
    // CSS optimizations for Instagram browser
    cssClasses: {
      // Ensure proper scrolling in Instagram browser
      container: isInstagram ? "instagram-browser-container" : "",
      
      // Better touch targets for Instagram browser
      buttons: isInstagram ? "instagram-browser-buttons" : "",
      
      // Optimized gallery for Instagram browser
      gallery: isInstagram ? "instagram-browser-gallery" : "",
      
      // Modal optimizations
      modal: isInstagram ? "instagram-browser-modal" : ""
    },
    
    // Behavioral optimizations
    behavior: {
      // Disable certain animations in Instagram browser for better performance
      disableAnimations: isInstagram,
      
      // Use different scroll behavior
      useNativeScroll: isInstagram,
      
      // Optimize video playback
      optimizeVideo: isInstagram,
      
      // Better modal handling
      improveModalHandling: isInstagram
    },
    
    // Technical info
    userAgent: navigator.userAgent,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight
    }
  };
}

/**
 * Apply Instagram browser CSS optimizations
 */
export function applyInstagramBrowserCSS(): void {
  if (typeof window === "undefined") return;

  const isInstagram = isInstagramBrowser();
  if (!isInstagram) return;

  // Create style element for Instagram browser optimizations
  const styleId = "instagram-browser-optimizations";
  
  // Remove existing styles
  const existingStyle = document.getElementById(styleId);
  if (existingStyle) {
    existingStyle.remove();
  }

  // Add Instagram browser specific styles
  const style = document.createElement("style");
  style.id = styleId;
  style.textContent = `
    /* Instagram Browser Optimizations */
    .instagram-browser-container {
      -webkit-overflow-scrolling: touch !important;
      overflow-x: hidden !important;
      position: relative !important;
      width: 100% !important;
      height: 100% !important;
    }
    
    .instagram-browser-buttons {
      touch-action: manipulation !important;
      -webkit-tap-highlight-color: transparent !important;
      user-select: none !important;
    }
    
    .instagram-browser-gallery {
      -webkit-transform: translateZ(0) !important;
      transform: translateZ(0) !important;
      will-change: transform !important;
    }
    
    .instagram-browser-modal {
      position: fixed !important;
      -webkit-transform: translate3d(0,0,0) !important;
      transform: translate3d(0,0,0) !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      z-index: 9999 !important;
      overflow: hidden !important;
      touch-action: pan-y pinch-zoom !important;
    }
    
    /* Lightbox content in Instagram - allow vertical scroll but handle horizontal swipes */
    .instagram-browser-modal > div {
      touch-action: pan-y pinch-zoom !important;
      -webkit-overflow-scrolling: touch !important;
    }
    
    /* Prevent body scroll when lightbox is open in Instagram */
    body:has(.instagram-browser-modal) {
      overflow: hidden !important;
      position: fixed !important;
      width: 100% !important;
      height: 100% !important;
    }
    
    /* Better video handling in Instagram browser */
    .instagram-browser-container video,
    .instagram-browser-container .vjs-tech {
      -webkit-playsinline: true !important;
      playsinline: true !important;
      x5-playsinline: true !important;
      x5-video-player-type: h5 !important;
      x5-video-player-fullscreen: false !important;
      object-fit: cover !important;
      width: 100% !important;
      height: 100% !important;
    }
    
    /* Touch targets for Instagram:
       - min-height 44px en botones de acción (CTA, play, primary).
       - NO forzar min-width: 44px en TODOS: rompe pills compactas
         (cookie Accept + X) y chips en 360px Android IG WebView.
       - Controles "compact" del cookie banner se excluyen a propósito. */
    .instagram-browser-buttons button,
    .instagram-browser-container button:not([data-cookie-compact]) {
      min-height: 44px !important;
      -webkit-tap-highlight-color: transparent !important;
      touch-action: manipulation !important;
    }
    .instagram-browser-container button[data-cookie-compact] {
      min-height: 36px !important;
      min-width: 0 !important;
      -webkit-tap-highlight-color: transparent !important;
      touch-action: manipulation !important;
    }
    .instagram-browser-container button[data-cookie-compact][aria-label] {
      min-width: 36px !important;
      min-height: 36px !important;
    }
    
    /* Prevent zoom on input focus */
    .instagram-browser-container input,
    .instagram-browser-container textarea,
    .instagram-browser-container select {
      font-size: 16px !important;
    }
    
    /* Viewport height fixes for Instagram - prevent half-screen blank */
    .instagram-browser-container {
      min-height: 100vh !important;
      min-height: 100dvh !important;
      min-height: -webkit-fill-available !important;
    }
    @supports (-webkit-touch-callout: none) {
      .instagram-browser-container {
        height: -webkit-fill-available !important;
        min-height: -webkit-fill-available !important;
      }
    }
    /* Fix fullscreen video container in Instagram WebView */
    .instagram-browser-modal {
      min-height: 100vh !important;
      min-height: 100dvh !important;
      min-height: -webkit-fill-available !important;
      height: 100% !important;
      height: 100vh !important;
      height: 100dvh !important;
      height: -webkit-fill-available !important;
    }
    
    /* Smooth scrolling for Instagram */
    .instagram-browser-container {
      scroll-behavior: smooth !important;
      -webkit-overflow-scrolling: touch !important;
    }
    
    /* Prevent text selection issues in Instagram */
    .instagram-browser-container * {
      -webkit-user-select: none !important;
      user-select: none !important;
    }
    
    .instagram-browser-container input,
    .instagram-browser-container textarea {
      -webkit-user-select: text !important;
      user-select: text !important;
    }
  `;
  
  document.head.appendChild(style);
}

/**
 * Track Instagram browser usage
 */
export function trackInstagramBrowserUsage(templateName: string, country: string): void {
  const optimizations = getInstagramOptimizations();
  
  if (optimizations.isInstagramBrowser) {
    // Track Instagram browser usage
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "instagram_browser_detected", {
        event_category: "browser_detection",
        event_label: `${templateName}_instagram_browser`,
        custom_parameter_1: country,
        custom_parameter_2: templateName,
        custom_parameter_3: "instagram_browser",
        custom_parameter_4: optimizations.isMobile ? "mobile" : "desktop"
      });
    }

  }
}

export default {
  isInstagramBrowser,
  isInstagramExplorer,
  isMobileDevice,
  getInstagramOptimizations,
  applyInstagramBrowserCSS,
  trackInstagramBrowserUsage,
};